import { FileDownloadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  LinearProgress,
  CardProps as MuiCardProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useIsMobile } from "../hooks/useIsMobile";

export interface ContainerCardExportAction {
  /** Button label text (used when iconOnly is false) */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Optional custom icon (defaults to FileDownloadOutlined) */
  icon?: React.ReactNode;
  /** When true, render only the icon without label text (useful for smaller cards) */
  iconOnly?: boolean;
}

export interface ContainerCardProps extends Omit<MuiCardProps, "title"> {
  /** The title of the card */
  title?: React.ReactNode;
  /** The subtitle of the card */
  subtitle?: React.ReactNode;
  /** Right-aligned action area in the header (e.g., for buttons or dropdowns) */
  action?: React.ReactNode;
  /** The content of the card */
  children?: React.ReactNode;
  /** Shows a LinearProgress bar at the top of the card while a background refetch is in progress */
  isFetching?: boolean;
  /** Optional export button shown on the top-right corner (desktop only) */
  exportAction?: ContainerCardExportAction;
}

export const ContainerCard: React.FC<ContainerCardProps> = ({
  title,
  subtitle,
  action,
  children,
  isFetching = false,
  exportAction,
  sx,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const showExport = !isMobile && !!exportAction;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
      {...props}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {title && (
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: 18,
                color: theme.colors.text[6],
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: 12,
                color: theme.colors.text[1],
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Render the action prop on the right side if it exists */}
          {action && <Box>{action}</Box>}
          {showExport &&
            (exportAction.iconOnly ? (
              <Tooltip placement="left" title={exportAction.label}>
                <Button
                  aria-label={exportAction.label}
                  data-testid="container-card-export-button"
                  onClick={exportAction.onClick}
                  size="small"
                  sx={{
                    color: theme.colors.secondary[5],
                    minWidth: "auto",
                    padding: "7px",
                    transition: "none",
                    "&:hover": {
                      backgroundColor: theme.colors.secondary[1],
                    },
                  }}
                  variant="text"
                >
                  {exportAction.icon ?? (
                    <FileDownloadOutlined fontSize="small" />
                  )}
                </Button>
              </Tooltip>
            ) : (
              <Button
                data-testid="container-card-export-button"
                onClick={exportAction.onClick}
                size="small"
                startIcon={
                  exportAction.icon ?? <FileDownloadOutlined fontSize="small" />
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  color: theme.colors.secondary[5],
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
                variant="secondary"
              >
                {exportAction.label}
              </Button>
            ))}
        </Box>
      </Box>
      {isFetching && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            borderRadius: "16px 16px 0 0",
            height: 3,
            zIndex: 1,
          }}
        />
      )}
      {children}
    </Box>
  );
};
